import React, { useContext, useReducer, useMemo, useEffect } from "react";
import { actions } from "../actions/cognito";
import { CONFIG } from "../config";
import cognitoUtils from "../utils/cognitoUtils";
import { isDeletedStorageTriggerLogout } from "../utils/functions";

const blankState = {
    admin: false,
    loading: false,
    authToken: null,
    idToken: null,
    user: null,
    userName: null,
    storage: null,
    codOperator: null,
    codOperator_b: null,
    pkOperador: null,
    pkOperador_b: null,
    updateAt: null,
    impersonated: false
};

const CognitoContext = React.createContext({
    state: null,
    dispatch: null,
});

/**
 * Función para inicializar el estado con el login de cognito.
 * 
 * @returns {Object}
 */
function getInitialState() {
    const cognito = cognitoUtils;
    const newState = { current: {} }
    cognito.checkSession(
        () => { newState.current = blankState },
        (session, user) => {
            const admin = window.localStorage.getItem(CONFIG.storage.admin) 
                ? JSON.parse(window.localStorage.getItem(CONFIG.storage.admin))
                : false;
            const codOperator = window.localStorage.getItem(CONFIG.storage.cod) || null;
            const codOperatorB = window.localStorage.getItem(CONFIG.storage.cod_b) || null;
            const pkOperador = window.localStorage.getItem(CONFIG.storage.pk) || null;
            const pkOperadorB = window.localStorage.getItem(CONFIG.storage.pk_b) || null;
            const impersonated = window.localStorage.getItem(CONFIG.storage.impersonated) 
                ? JSON.parse(window.localStorage.getItem(CONFIG.storage.impersonated)) 
                : false;
            let updatedAt = '';
            try {
                updatedAt = window.localStorage.getItem(CONFIG.storage.updatedAt) ? JSON.parse(window.localStorage.getItem(CONFIG.storage.updatedAt)) : null;
            } catch(e) {
                console.log(e);
            }
            newState.current = { 
                admin: admin,
                authToken: session.credentials.authToken, 
                idToken: session.credentials.idToken, 
                userName: user.username, 
                codOperator: codOperator, 
                codOperator_b: codOperatorB, 
                pkOperador: pkOperador,
                pkOperador_b: pkOperadorB,
                updateAt: updatedAt,
                impersonated: impersonated
            }
        }
    )
    return newState.current;
}

/**
 * Función para hacer sign in y actualizar el estado acordemente
 * 
 * @param {Object} payload Los datos recibidos a través del dispatch
 * @returns {Object}
 */
function logIn(payload) {
    const prefix = payload.user.keyPrefix;
    const token = payload.user.storage[`${prefix}.${payload.user.username}.accessToken`]
    const idToken = payload.user.storage[`${prefix}.${payload.user.username}.idToken`]; 
    window.localStorage.setItem(CONFIG.storage.cod_b, payload.codOperador);
    const newState = {
        admin: false,
        loading: false,
        authToken: token,
        idToken: idToken,
        userName: payload.user.username,
        codOperator: payload.codOperador, 
        codOperator_b: payload.codOperador, 
        pkOperador: null,
        pkOperador_b: null,
        impersonated: false
    }
    return newState;
}

/**
 * Función para hacer sign out.
 * 
 * @param {Object} state El estado actual. Como el sign out hace una redirección a login no es necesario que modifiquemos el estado, ya que al volver a entrar, se volverá a inicializar con los valores por defecto.
 * @returns {Object}
 */
function logOut(state) {
    const cognito = cognitoUtils;
    cognito.signOutCognitoSession();
    window.localStorage.removeItem(CONFIG.storage.cod);
    window.localStorage.removeItem(CONFIG.storage.pk);
    window.localStorage.removeItem(CONFIG.storage.updatedAt);
    window.localStorage.removeItem(CONFIG.storage.cod_b);
    window.localStorage.removeItem(CONFIG.storage.pk_b);
    window.localStorage.removeItem(CONFIG.storage.impersonated);
    return state;
}

/**
 * Función para realizar la impersonacion.
 * @param {Object} state El estado actual. 
 * @param {Object} payload Los datos recibidos a través del dispatch
 * @returns {Object} 
 */
function impersonate(state, payload) {
    const codOperator = payload.codOperator; 
    const pkOperador = payload.pkOperador; 
    window.localStorage.setItem(CONFIG.storage.impersonated, true);
    window.localStorage.setItem(CONFIG.storage.cod, codOperator);
    window.localStorage.setItem(CONFIG.storage.pk, pkOperador);
    return { ...state, impersonated: true, codOperator: codOperator, pkOperador: pkOperador }
}

/**
 * Función para desconectarse del usuario impersonado.
 * @param {Object} state El estado actual. 
 * @param {Object} payload Los datos recibidos a través del dispatch
 * @returns {Object} 
 */
function logoutImpersonate(state) {
    const codOperator = window.localStorage.getItem(CONFIG.storage.cod_b); 
    const pkOperador = window.localStorage.getItem(CONFIG.storage.pk_b); 
    window.localStorage.setItem(CONFIG.storage.impersonated, false);
    window.localStorage.setItem(CONFIG.storage.cod, codOperator);
    window.localStorage.setItem(CONFIG.storage.pk, pkOperador);
    return { ...state, impersonated: false, codOperator: codOperator, pkOperador: pkOperador }
}

/**
 * Función para setear el pkOPerador.
 * @param {Object} state El estado actual. 
 * @param {String} payload El código. 
 * @returns {Object}
 */
function getPkOperador(state, payload) {
    const admin = payload.admin;
    const pkOperador = payload.pkOperador
    window.localStorage.setItem(CONFIG.storage.admin, admin);
    window.localStorage.setItem(CONFIG.storage.pk, pkOperador);
    window.localStorage.setItem(CONFIG.storage.pk_b, pkOperador);
    return { ...state, pkOperador: pkOperador, pkOperador_b: pkOperador, admin: admin }
}

/**
 * Función para setear estado de carga. 
 * @param {Object} state El estado actual. 
 * @param {String} payload El código. 
 * @returns {Object}
 */
function loading(state, payload) {
    return { ...state, loading: payload }
}

/**
 * Función para guardar la fecha de modificación.
 * 
 * @param {Object} state El estado actual. 
 * @param {String} payload El código. 
 * @returns {Object}
 */
function updateAt(state, payload) {
    window.localStorage.setItem(CONFIG.storage.updatedAt, JSON.stringify(payload));
    return { ...state, updateAt: payload }
}

/**
 * Función cargar los datos iniciales: codOperador, pkOperador, nombre del operador y fecha de última modificación. 
 * @param {Object} state El estado actual. 
 * @param {String} payload El código. 
 * @returns {Object}
 */
function loadInitialData(state, payload) {
    const codOperator = payload.codOperator;
    const pkOperador = payload.pkOperador;
    const updateAt = payload.updateAt;
    window.localStorage.setItem(CONFIG.storage.pk, pkOperador);
    window.localStorage.setItem(CONFIG.storage.updatedAt, JSON.stringify(updateAt));
    window.localStorage.setItem(CONFIG.storage.cod_b, codOperator);
    window.localStorage.setItem(CONFIG.storage.pk_b, pkOperador);
    return { ...state, codOperator: codOperator, pkOperador: pkOperador, updateAt: updateAt };
}

function reducer(state, action) {
    switch (action.type) {
        case actions.LOADING:
            return loading(state, action.payload);
        case actions.LOG_IN:
            return logIn(action.payload);
        case actions.LOG_OUT:
            return logOut(state);
        case actions.PK_OPERADOR:
            return getPkOperador(state, action.payload);
        case actions.IMPERSONATE: 
            return impersonate(state, action.payload);
        case actions.LOG_OUT_IMPERSONATE:
            return logoutImpersonate(state);
        case actions.UPDATED_AT:
            return updateAt(state, action.payload);
        case actions.LOAD_INITIAL_DATA: 
            return loadInitialData(state, action.payload);
        default:
            return state;
    }
}

export const CognitoProvider = ({ children }) => {

    const [state, dispatch] = useReducer(reducer, getInitialState());  

    /**
     * Función para deslogar al usuario cuando se borra una cookie de cognito.
     * @param {Event} event El evento nativo del navegador
     */
    const checkStorage = (event) => {

        if(event.storageArea.length === 0 || (event.key.includes('Cognito') && event.newValue === null) || (isDeletedStorageTriggerLogout(event.key) && event.newValue === null)) {
            dispatch({ type: actions.LOG_OUT });
        }
    }

    useEffect(() => {
        window.addEventListener('storage', checkStorage); //
        return () => { window.removeEventListener('storage', checkStorage) } // Eliminamos el listener al desmontar el componente
    }, []);

    const contextValue = useMemo(() => {
        return { state, dispatch };
    }, [state, dispatch]);

    return <CognitoContext.Provider value={contextValue}>
        {children}
    </CognitoContext.Provider>
}

export const useCognitoProvider = () => useContext(CognitoContext);